import { Controller } from "@hotwired/stimulus"
import Cropper from "cropperjs"
import { CROPPER_TEMPLATE } from "./image_cropper_controller"

export default class extends Controller {
  static targets = [
    "input",
    "name",
    "pendingPreviews",
    "template",
    "removalFields",
    "cropPanel",
    "cropStage",
    "cropImage",
    "cropStatus",
    "applyCropButton",
  ]

  connect() {
    this.entries = []
    this.objectUrls = new Map()
    this.cropper = null
    this.cropSourceUrl = null
    this.currentEntry = null
    this.cropTimeout = null
    this.cropVersion = 0
    this.scheduleCrop = this.scheduleCrop.bind(this)
  }

  disconnect() {
    this.destroyCropper()
    this.revokeCropSourceUrl()
    this.revokeObjectUrls()
  }

  async add() {
    const incomingFiles = Array.from(this.inputTarget.files || [])
    this.clearInput()
    if (incomingFiles.length === 0) return

    this.nameTarget.textContent = "Připravuji fotografie…"
    const addedEntries = []

    try {
      for (const sourceFile of incomingFiles) {
        const key = this.fileKey(sourceFile)
        if (this.entries.some((entry) => entry.key === key)) continue

        const outputFile = await this.centeredSquareImage(sourceFile)
        const entry = { key, sourceFile, outputFile }
        this.entries.push(entry)
        addedEntries.push(entry)
      }
    } catch {
      this.nameTarget.textContent = "Fotografie se nepodařilo připravit. Vyberte jiné soubory."
      this.syncInput()
      return
    }

    this.syncInput()
    this.renderPreviews()
    if (addedEntries.length > 0) this.openEditor(addedEntries[0].key)
  }

  editSelected(event) {
    const preview = event.currentTarget.closest("[data-gallery-preview]")
    this.openEditor(preview?.dataset.galleryFileKey)
  }

  async applyCrop(event) {
    event.preventDefault()
    this.applyCropButtonTarget.disabled = true
    this.applyCropButtonTarget.textContent = "Ukládám ořez…"

    const saved = await this.writeCroppedFile()
    this.applyCropButtonTarget.disabled = false
    this.applyCropButtonTarget.textContent = "Použít ořez"
    if (saved) this.closeEditor()
  }

  removeSelected(event) {
    const preview = event.currentTarget.closest("[data-gallery-preview]")
    const key = preview?.dataset.galleryFileKey
    if (!key) return

    if (this.currentEntry?.key === key) this.closeEditor()
    this.entries = this.entries.filter((entry) => entry.key !== key)
    this.syncInput()
    this.renderPreviews()
  }

  removeStored(event) {
    const button = event.currentTarget
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = button.dataset.removalName
    input.value = button.dataset.removalValue
    this.removalFieldsTarget.append(input)
    button.closest(".file-upload-preview")?.remove()
  }

  removeCached(event) {
    event.currentTarget.closest(".file-upload-preview")?.remove()
  }

  openEditor(key) {
    const entry = this.entries.find((candidate) => candidate.key === key)
    if (!entry) return

    this.destroyCropper()
    this.revokeCropSourceUrl()
    const cropVersion = this.cropVersion
    this.currentEntry = entry
    this.cropSourceUrl = URL.createObjectURL(entry.sourceFile)
    this.cropImageTarget.onload = () => {
      if (cropVersion !== this.cropVersion || this.currentEntry !== entry) return

      this.cropPanelTarget.hidden = false
      this.cropper = new Cropper(this.cropImageTarget, {
        container: this.cropStageTarget,
        template: CROPPER_TEMPLATE,
      })
      this.cropper.getCropperSelection()?.addEventListener("change", this.scheduleCrop)
      this.cropper.getCropperCanvas()?.addEventListener("actionend", this.scheduleCrop)
      this.cropStatusTarget.textContent = "Vyberte čtvercový výřez. Výsledek bude mít 1000 × 1000 px."
    }
    this.cropImageTarget.src = this.cropSourceUrl
  }

  closeEditor() {
    this.destroyCropper()
    this.revokeCropSourceUrl()
    this.currentEntry = null
    this.cropPanelTarget.hidden = true
    this.cropImageTarget.removeAttribute("src")
    this.cropStatusTarget.textContent = ""
  }

  scheduleCrop() {
    clearTimeout(this.cropTimeout)
    this.cropTimeout = setTimeout(() => this.writeCroppedFile(), 250)
  }

  async writeCroppedFile() {
    const selection = this.cropper?.getCropperSelection()
    const entry = this.currentEntry
    if (!selection || !entry) return false

    try {
      const cropVersion = ++this.cropVersion
      const canvas = await selection.$toCanvas({ width: 1000, height: 1000 })
      if (cropVersion !== this.cropVersion) return false

      const blob = await this.canvasBlob(canvas)
      if (cropVersion !== this.cropVersion) return false

      entry.outputFile = this.outputFile(blob, entry.sourceFile)
      this.syncInput()
      this.renderPreviews()
      this.cropStatusTarget.textContent = "Ořez je připravený k uložení."
      return true
    } catch {
      this.cropStatusTarget.textContent = "Ořez se nepodařilo připravit."
      return false
    }
  }

  renderPreviews() {
    this.revokeObjectUrls()
    this.pendingPreviewsTarget.replaceChildren()

    this.entries.forEach((entry) => {
      const fragment = this.templateTarget.content.cloneNode(true)
      const preview = fragment.querySelector("[data-gallery-preview]")
      const image = fragment.querySelector("[data-gallery-preview-image]")
      const name = fragment.querySelector("[data-gallery-preview-name]")
      const objectUrl = URL.createObjectURL(entry.outputFile)

      preview.dataset.galleryFileKey = entry.key
      image.src = objectUrl
      image.alt = entry.outputFile.name
      name.textContent = entry.outputFile.name
      this.objectUrls.set(entry.key, objectUrl)
      this.pendingPreviewsTarget.append(fragment)
    })

    this.pendingPreviewsTarget.hidden = this.entries.length === 0
    this.updateName()
  }

  syncInput() {
    const dataTransfer = new DataTransfer()
    this.entries.forEach((entry) => dataTransfer.items.add(entry.outputFile))
    this.inputTarget.files = dataTransfer.files
  }

  clearInput() {
    this.inputTarget.files = new DataTransfer().files
  }

  async centeredSquareImage(sourceFile) {
    const bitmap = await createImageBitmap(sourceFile, { imageOrientation: "from-image" })
    const sourceSize = Math.min(bitmap.width, bitmap.height)
    const sourceX = (bitmap.width - sourceSize) / 2
    const sourceY = (bitmap.height - sourceSize) / 2
    const canvas = document.createElement("canvas")
    canvas.width = 1000
    canvas.height = 1000
    canvas.getContext("2d").drawImage(bitmap, sourceX, sourceY, sourceSize, sourceSize, 0, 0, 1000, 1000)
    bitmap.close()

    return this.outputFile(await this.canvasBlob(canvas), sourceFile)
  }

  canvasBlob(canvas) {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("Image conversion failed")), "image/jpeg", 0.92)
    })
  }

  outputFile(blob, sourceFile) {
    return new File(
      [blob],
      sourceFile.name.replace(/\.[^.]+$/, "") + "-1000x1000.jpg",
      { type: "image/jpeg", lastModified: sourceFile.lastModified },
    )
  }

  updateName() {
    if (this.entries.length === 0) {
      this.nameTarget.textContent = "Nebyl vybrán žádný soubor"
    } else if (this.entries.length === 1) {
      this.nameTarget.textContent = this.entries[0].outputFile.name
    } else {
      this.nameTarget.textContent = `Vybráno souborů: ${this.entries.length}`
    }
  }

  fileKey(file) {
    return `${file.name}:${file.size}:${file.lastModified}`
  }

  destroyCropper() {
    clearTimeout(this.cropTimeout)
    this.cropVersion += 1
    this.cropImageTarget.onload = null
    this.cropper?.getCropperSelection()?.removeEventListener("change", this.scheduleCrop)
    this.cropper?.getCropperCanvas()?.removeEventListener("actionend", this.scheduleCrop)
    this.cropper?.destroy()
    this.cropper = null
  }

  revokeCropSourceUrl() {
    if (!this.cropSourceUrl) return

    URL.revokeObjectURL(this.cropSourceUrl)
    this.cropSourceUrl = null
  }

  revokeObjectUrls() {
    this.objectUrls?.forEach((url) => URL.revokeObjectURL(url))
    this.objectUrls?.clear()
  }
}
